import { http, HttpResponse } from 'msw';

import { CountryCode, toCountryCode } from '../../types/CountryCode';
import { Kyc, Survey } from '../../types/KycTypes';
import { KYC_COUNTRIES_DK, KYC_COUNTRIES_NO } from './kycCountriesFixtures';

/**
 * Mirrors the know-your-customer-service v4 onboarding template, as served through the proxy: question ids,
 * types, display conditions and the presence or absence of `info` all match a real capture.
 *
 * NO (`templateVersion: 25`) and DK (`templateVersion: 5`) both have real captures, from LP-2995. Markets other
 * than NO/DK have no capture yet and fall back to the NO fixture in the handler below.
 *
 * Keeping this faithful matters more than keeping it short. The v1 shape this replaced had the citizenship group as
 * alternating COUNTRY_CODE_SELECTION/BUTTON questions, which is what KycCitizenshipGroup was built around; because the
 * mock still served that shape, demo mode and CI kept passing while production served 700/703/702 and dead-ended the
 * customer on a "next" button that did nothing.
 */

const PEP_POSITION_OPTIONS = [
    { id: 1, label: 'Stats- eller regjeringssjef, minister samt vise- og assisterende minister' },
    { id: 2, label: 'Parlamentsmedlem/stortingsrepresentant' },
    { id: 3, label: 'Medlem av styret i politiske partier' },
    {
        id: 4,
        label:
            'Dommer i Høyesterett, konstitusjonelle domstoler eller andre rettslige organer på høyt nivå, hvis ' +
            'avgjørelser kun unntaksvis kan ankes',
    },
    {
        id: 5,
        label: 'Høytstående tjenestemann i revisjonsmyndighet og medlem av sentralbankers styrende organer',
    },
    { id: 6, label: 'Ambassadør, utsendingssjef samt høy offiser i forsvaret' },
    { id: 7, label: 'Medlem av forvaltnings-, ledelses- eller kontrollorgan i statseide foretak' },
    { id: 8, label: 'Medlem av ledelsen i en internasjonal organisasjon (for eksempel FN, NATO, WTO)' },
];

const COUNTRY_OR_ORGANISATION_INFO = { description: 'F.eks. Sverige, EU, FN' };
const MULTIPLE_ANSWERS_INFO = { description: 'Flere svaralternativer er mulig' };

const NEW_ACCOUNT_GROUP_NO: Survey = {
    id: 'new-account',
    title: 'Spørsmål om lånet ditt',
    type: 'LOANS',
    questions: [
        {
            label: 'Hva er formålet med lånet?',
            name: 502,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 1, label: 'Refinansiering/samlelån (innfri dyre kreditter)' },
                { id: 2, label: 'Bil/bobil/båt eller lignende' },
                { id: 3, label: 'Renovering/energitiltak i bolig' },
                { id: 4, label: 'Møbler/hvitevarer/elektronikk' },
                { id: 5, label: 'Flytting/depositum/oppstartskostnader for bolig' },
                { id: 6, label: 'Egenkapital til bolig' },
                { id: 7, label: 'Reise/ferie' },
                { id: 8, label: 'Bryllup/større høytid' },
                { id: 9, label: 'Tannbehandling/helsetjenester' },
                { id: 10, label: 'Studier/utdanning (ikke studielån)' },
                { id: 11, label: 'Barnerelaterte kostnader' },
                { id: 12, label: 'Skatt/avgifter' },
                { id: 13, label: 'Oppstart av virksomhet/næringsvirksomhet' },
                { id: 14, label: 'Investeringer/verdipapirer/krypto' },
                { id: 15, label: 'Spill/betting' },
                { id: 16, label: 'Annet' },
            ],
            displayCondition: null,
            info: MULTIPLE_ANSWERS_INFO,
        },
        {
            label: 'Angi annet formål',
            name: 5000,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 502, optionId: 16 },
            info: null,
        },
        {
            label:
                'Vil ekstra nedbetalinger (betalinger utover nedbetalingsplanen) bli gjennomført i løpet av lånets ' +
                'løpetid?',
            name: 5031,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Ja' },
                { id: 2, label: 'Nei' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Hvor ofte kommer innbetalingene på lånet til å foretas?',
            name: 500,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: '0 - 5 ganger per år' },
                { id: 2, label: '6 - 15 ganger per år' },
                { id: 3, label: 'Flere enn 15 ganger per år' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Hvor kommer midlene fra som brukes til tilbakebetaling av lånet?',
            name: 5034,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 1, label: 'Lønn' },
                { id: 2, label: 'Offentlige ytelser' },
                { id: 3, label: 'Pensjon' },
                { id: 4, label: 'Annen forsikring (f.eks. sykeforsikring eller inntektsforsikring)' },
                { id: 5, label: 'Overskudd/inntekter fra egen virksomhet' },
                { id: 6, label: 'Avkastning på kapital' },
                { id: 7, label: 'Arv/gave' },
                { id: 8, label: 'Gevinst fra spill/lotteri' },
                { id: 9, label: 'Eiendomssalg' },
                { id: 10, label: 'Salg av virksomhet' },
                { id: 11, label: 'Salg av bil/båt' },
                { id: 12, label: 'Annet' },
            ],
            displayCondition: null,
            info: MULTIPLE_ANSWERS_INFO,
        },
        {
            label: 'Beskriv kort hva annet er',
            name: 5033,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 5034, optionId: 12 },
            info: null,
        },
        {
            label: 'Hvem kommer til å foreta innbetalingene? ',
            name: 501,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 1, label: 'Kontoeier' },
                { id: 2, label: 'Ev. medlånetaker' },
                { id: 3, label: 'Verge/forvalter/bemyndiget' },
                { id: 4, label: 'Annen' },
            ],
            displayCondition: null,
            info: MULTIPLE_ANSWERS_INFO,
        },
        {
            label: 'Angi hvem som vil foreta tilbakebetalinger på lånet',
            name: 5010,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 501, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi forhold til tilbakebetaler',
            name: 5035,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 501, optionId: 4 },
            info: { description: 'F.eks. ektefelle, barn, forelder, venn, kollega' },
        },
        {
            label: 'Angi formålet med at en annen tilbakebetaler finnes',
            name: 5032,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 501, optionId: 4 },
            info: null,
        },
    ],
};

const ECONOMY_GROUP_NO: Survey = {
    id: 'ECONOMY',
    title: 'Spørsmål om din økonomi',
    type: 'ECONOMY',
    questions: [
        {
            label: 'Hva er din totale inntekt per måned (før skatt)?',
            name: 100,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Jeg har ingen inntekt' },
                { id: 2, label: 'Opptil 29.000 NOK' },
                { id: 3, label: '30.000 - 49.999 NOK' },
                { id: 6, label: '50.000 - 69.999 NOK' },
                { id: 4, label: '70 000 - 99 999 NOK' },
                { id: 5, label: 'Mer enn 100.000 NOK' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Hvor kommer inntekten din fra?',
            name: 101,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 1, label: 'Lønn fra ansettelse' },
                { id: 3, label: 'Egen bedrift eller selvstendig virksomhet (lønn, overskudd eller utbytte)' },
                { id: 4, label: 'Pensjon' },
                { id: 5, label: 'Studiestøtte (Lånekassen)' },
                { id: 6, label: 'Arbeidsledighetstrygd eller offentlig støtte' },
                { id: 7, label: 'Erstatning eller forsikringsutbetaling (f.eks. sykepenger, foreldrepenger)' },
                { id: 9, label: 'Kapitalinntekter eller avkastning på investeringer' },
                { id: 10, label: 'Leie- eller utleieinntekter' },
                { id: 11, label: 'Arv eller gave' },
                { id: 8, label: 'Annet' },
            ],
            displayCondition: null,
            info: MULTIPLE_ANSWERS_INFO,
        },
        {
            label: 'Skriv inn inntektskilde',
            name: 1010,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 101, optionId: 8 },
            info: null,
        },
    ],
};

const CITIZENSHIP_GROUP_NO: Survey = {
    id: 'CITIZENSHIP',
    title: 'Spørsmål om statsborgerskap',
    type: 'CITIZENSHIP',
    questions: [
        {
            label: 'Oppgi ditt statsborgerskap',
            name: 700,
            type: 'COUNTRY_CODE_SELECTION',
            options: null,
            displayCondition: null,
            info: null,
        },
        {
            label: 'Har du flere statsborgerskap?',
            name: 703,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Ja' },
                { id: 2, label: 'Nei' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Vennligst oppgi hvilket eller hvilke land',
            name: 702,
            type: 'MULTIPLE_COUNTRY_CODE_SELECTION',
            options: null,
            displayCondition: { questionId: 703, optionId: 1 },
            info: null,
        },
    ],
};

const NEW_ACCOUNT_GROUP_DK: Survey = {
    id: 'new-account',
    title: 'Spørgsmål om dit lån',
    type: 'LOANS',
    questions: [
        {
            label: 'Hvad er formålet med lånet?',
            name: 500,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 16, label: 'Samlelån/indfrielse af dyre kreditter' },
                { id: 15, label: 'Bil/bobil/båd eller lignende' },
                { id: 14, label: 'Renovering/energitiltag i bolig' },
                { id: 1, label: 'Møbler/hvidevarer/elektronik' },
                { id: 2, label: 'Investering (f.eks. aktier)' },
                { id: 3, label: 'Egenkapital til bolig' },
                { id: 5, label: 'Rejse/ferie' },
                { id: 4, label: 'Bryllup/større højtid' },
                { id: 6, label: 'Tandbehandling/sundhedsudgifter' },
                { id: 7, label: 'Studier/uddannelse (ikke studielån)' },
                { id: 8, label: 'Børnerelaterede udgifter' },
                { id: 9, label: 'Skat/afgifter' },
                { id: 10, label: 'Virksomhedsstart/næringsvirksomhed' },
                { id: 11, label: 'Investeringer/ værdipapirer/krypto' },
                { id: 12, label: 'Spil/betting' },
                { id: 13, label: 'Andet' },
            ],
            displayCondition: null,
            info: { description: 'Flere svarmuligheder er mulige' },
        },
        {
            label: 'Oplys hvad',
            name: 5000,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 500, optionId: 13 },
            info: null,
        },
        {
            label:
                'Vil ekstraordinære afdrag (betalinger ud over betalingsplanen) blive gennemført i løbet af lånets ' +
                'løbetid?',
            name: 5032,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Ja' },
                { id: 2, label: 'Nej' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Hvor ofte vil ekstraordinære afdrag blive gennemført?',
            name: 5033,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: '0 - 5 gange om året' },
                { id: 2, label: '6 - 15 gange om året' },
                { id: 3, label: 'Mere end 15 gange om året' },
            ],
            displayCondition: { questionId: 5032, optionId: 1 },
            info: null,
        },
        {
            label: 'Hvor kommer de midler fra, som bruges til tilbagebetaling af lånet?',
            name: 5034,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Løn' },
                { id: 2, label: 'Offentlige ydelser' },
                { id: 3, label: 'Pension' },
                { id: 4, label: 'Anden forsikring (f.eks. syge- eller indkomstforsikring)' },
                { id: 5, label: 'Overskud/indtægter fra egen virksomhed' },
                { id: 6, label: 'Afkast af kapital' },
                { id: 7, label: 'Arv/gave' },
                { id: 8, label: 'Gevinst fra spil/lotteri' },
                { id: 9, label: 'Ejendomssalg' },
                { id: 10, label: 'Salg af virksomhed' },
                { id: 11, label: 'Salg af bil/båd' },
                { id: 12, label: 'Andet' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Beskriv kort, hvad andet er',
            name: 5035,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 5034, optionId: 12 },
            info: null,
        },
        {
            label: 'Hvem kommer til at foretage indbetalinger på lånet?',
            name: 502,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 1, label: 'Kontohaver' },
                { id: 2, label: 'Med-kontohaver' },
                { id: 3, label: 'Værge' },
                { id: 4, label: 'Andet' },
            ],
            displayCondition: null,
            info: { description: 'Flere svarmuligheder er mulige' },
        },
        {
            label: 'Angiv hvem der vil foretage tilbagebetalinger på lånet',
            name: 5021,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 502, optionId: 4 },
            info: null,
        },
        {
            label: 'Angiv relation til tilbagebetaler',
            name: 5036,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 502, optionId: 4 },
            info: { description: 'F.eks. ægtefælle, barn, forælder, ven, kollega' },
        },
        {
            label: 'Angiv formålet med, at en anden tilbagebetaler findes',
            name: 5037,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 502, optionId: 4 },
            info: null,
        },
    ],
};

const ECONOMY_GROUP_DK: Survey = {
    id: 'ECONOMY',
    title: 'Spørgsmål om din økonomi',
    type: 'ECONOMY',
    questions: [
        {
            label: 'Hvad er din samlede månedlige indkomst før skat?',
            name: 100,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Jeg har ingen indkomst' },
                { id: 2, label: 'Op til 25 000 DKK' },
                { id: 3, label: '25 001 - 40 000 DKK' },
                { id: 4, label: '40 001 - 70 000 DKK' },
                { id: 5, label: 'Mere end 70 000 DKK' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Hvorfra kommer din indkomst?',
            name: 101,
            type: 'MULTIPLE_SELECTION',
            options: [
                { id: 1, label: 'Løn eller Pension' },
                { id: 9, label: 'Kontanthjælp/understøttelse' },
                { id: 4, label: 'Indkomst af egen virksomhed' },
                { id: 5, label: 'Kapitalindkomst' },
                { id: 6, label: 'SU/studielån' },
                { id: 7, label: 'Arbejdsløshedsunderstøttelse' },
                { id: 8, label: 'Andet' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Oplys hvilken indkomst',
            name: 1010,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 101, optionId: 8 },
            info: null,
        },
    ],
};

const CITIZENSHIP_GROUP_DK: Survey = {
    id: 'CITIZENSHIP',
    title: 'Spørgsmål om statsborgerskab',
    type: 'CITIZENSHIP',
    questions: [
        {
            label: 'Angiv dit statsborgerskab',
            name: 700,
            type: 'COUNTRY_CODE_SELECTION',
            options: null,
            displayCondition: null,
            info: null,
        },
        {
            label: 'Jeg har flere statsborgerskaber',
            name: 701,
            type: 'BUTTON',
            options: [
                { id: 1, label: 'Ja' },
                { id: 2, label: 'Nej' },
            ],
            displayCondition: null,
            info: null,
        },
        {
            label: 'Venligst angiv hvilket/hvilke lande?',
            name: 703,
            type: 'MULTIPLE_COUNTRY_CODE_SELECTION',
            options: null,
            displayCondition: { questionId: 701, optionId: 1 },
            info: null,
        },
    ],
};

/**
 * The PEP group is only served through the separate `onlyPep=true` call (see `PepQuestionsFull` below) -- the real
 * base survey response does not include it. It's still built from the older, single (pre-LP-2995) capture: nobody
 * has captured what the real `onlyPep=true` response looks like per country, so this stays shared across markets
 * and un-versioned relative to the NO/DK split below. Re-check this against a real capture if/when one exists.
 *
 * One deliberate departure from that capture: the dev template's PEP position lists carry placeholder labels ("x",
 * "xParlamentsmedlem") on some questions and clean ones on others, so the clean list is used throughout.
 *
 * Everything else is verbatim, including copy that looks like a mistake -- the unbalanced `</p>` that opens the PEP
 * info HTML, and "Tex." rather than "F.eks." on question 318. Tidying those here would make this a worse reference:
 * the point of the fixture is that what you read is what the service sends. They belong upstream, in the template.
 */
const PEP_GROUP: Survey = {
    id: 'PEP',
    title: 'Spørsmål om PEP/nært familiemedlem eller kjent medarbeider til en PEP',
    type: 'PEP',
    questions: [
        {
            label:
                'Er du (eller har du i løpet av de siste 18 månedene vært) en person i politisk utsatt stilling ' +
                '(PEP), eller er du et familiemedlem eller en kjent medarbeider av en slik person (RCA)?',
            name: 310,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Nei' },
                { id: 2, label: 'Ja' },
            ],
            displayCondition: null,
            info: {
                label: 'Les mer om PEP',
                description:
                    "Med PEP (politisk eksponert person) menes personer som har, eller har hatt en viktig offentlig funksjon i en stat eller i en internasjonal organisasjon.</p><p>Nedenfor ser du hvilke stillinger/verv som anses å være en <b>PEP</b> samt hvilke relasjoner som omfattes.</p><ul><li>Statsoverhode, regjeringssjef, minister eller assisterende minister</li><li>Medlem av nasjonalforsamling</li><li>Medlem av styrende organ i politisk parti</li><li>Medlem av høyere rettsinstans som treffer beslutning som ikke eller bare unntaksvis kan ankes </li><li>Medlem av styre i riksrevisjon, revisjonsdomstol eller sentralbank </li><li>Ambassadør, chargé d'affaires eller militær offiser av høyere rang</li><li>Medlem av administrativt, ledende eller kontrollerende organ i statlig foretak</li><li>Direktør, styremedlem eller annen person i øverste ledelse i internasjonal organisasjon</li><li>Med <b>nært familiemedlem</b> menes foreldre, ektefelle, registrert partner, samboer og barn, samt barns ektefelle, registrert partner eller samboer</li><li>Med <b>kjent medarbeider</b> menes fysisk person som er kjent for å:</li><ul><li>være reell rettighetshaver i juridisk person, sammenslutning eller utenlandsk arrangement i fellesskap med politisk eksponert person</li><li>ha nær forretningsforbindelse til politisk eksponert person</li><li>være eneste reelle rettighetshaver i juridisk person, sammenslutning eller utenlandsk juridisk arrangement som i realiteten er etablert for å begunstige politisk eksponert person</li></ul></ul>",
            },
        },
        {
            label: 'Hvem er PEP?',
            name: 311,
            type: 'SINGLE_SELECTION',
            options: [
                { id: 1, label: 'Jeg er selv PEP' },
                { id: 2, label: 'Jeg er nært familiemedlem til en PEP' },
                { id: 3, label: 'Jeg er kjent medarbeider til en PEP' },
                {
                    id: 4,
                    label: 'Jeg er selv en PEP og jeg er nært familiemedlem eller kjent medarbeider til en PEP',
                },
            ],
            displayCondition: { questionId: 310, optionId: 2 },
            info: null,
        },
        {
            label: 'Angi din Stilling',
            name: 320,
            type: 'MULTIPLE_SELECTION',
            options: PEP_POSITION_OPTIONS,
            displayCondition: { questionId: 311, optionId: 1 },
            info: null,
        },
        {
            label: 'Angi din tittel',
            name: 301,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 1 },
            info: null,
        },
        {
            label: 'Angi land eller organisasjon for PEP',
            name: 318,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 1 },
            info: { description: 'Tex. Sverige, EU, FN' },
        },
        {
            label: 'Fullstendig navn PEP',
            name: 328,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 2 },
            info: null,
        },
        {
            label: 'Fødselsnummer eller fødselsdato for PEP',
            name: 325,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 2 },
            info: null,
        },
        {
            label: 'Angi din stilling',
            name: 332,
            type: 'SINGLE_SELECTION',
            options: PEP_POSITION_OPTIONS,
            displayCondition: { questionId: 311, optionId: 2 },
            info: null,
        },
        {
            label: 'Angi tittel for PEP',
            name: 305,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 2 },
            info: null,
        },
        {
            label: 'Angi land eller organisasjon for PEP',
            name: 304,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 2 },
            info: COUNTRY_OR_ORGANISATION_INFO,
        },
        {
            label: 'Fullstendig navn PEP',
            name: 329,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 3 },
            info: null,
        },
        {
            label: 'Fødselsnummer eller fødselsdato for PEP',
            name: 326,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 3 },
            info: null,
        },
        {
            label: 'Angi stilling for PEP',
            name: 322,
            type: 'MULTIPLE_SELECTION',
            options: PEP_POSITION_OPTIONS,
            displayCondition: { questionId: 311, optionId: 3 },
            info: null,
        },
        {
            label: 'Angi tittel for PEP',
            name: 309,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 3 },
            info: null,
        },
        {
            label: 'Angi land eller organisasjon for PEP',
            name: 307,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 3 },
            info: COUNTRY_OR_ORGANISATION_INFO,
        },
        {
            label: 'Oppgi relasjon til PEP',
            name: 331,
            type: 'SINGLE_SELECTION_DROPDOWN',
            options: [
                { id: 1, label: 'Familiemedlem' },
                { id: 2, label: 'Kjent medarbeider' },
            ],
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi din stilling',
            name: 324,
            type: 'MULTIPLE_SELECTION',
            options: PEP_POSITION_OPTIONS,
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi din tittel',
            name: 315,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi land eller organisasjon for deg som PEP',
            name: 316,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 4 },
            info: COUNTRY_OR_ORGANISATION_INFO,
        },
        {
            label: 'Fullstendig navn PEP',
            name: 330,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Fødselsnummer eller fødselsdato for PEP',
            name: 327,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi stilling for PEP',
            name: 323,
            type: 'MULTIPLE_SELECTION',
            options: PEP_POSITION_OPTIONS,
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi tittel for PEP',
            name: 317,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 4 },
            info: null,
        },
        {
            label: 'Angi land eller organisasjon for PEP',
            name: 319,
            type: 'FREE_TEXT',
            options: null,
            displayCondition: { questionId: 311, optionId: 4 },
            info: COUNTRY_OR_ORGANISATION_INFO,
        },
    ],
};

/**
 * Added by consumerloan-application-service from the v2 countries endpoint rather than by the survey call, which is
 * why the ids are lowercase here and uppercase in the unrelated country-of-birth list. Kept as the short
 * SE/NO/DK/FI/DE list it always was: unlike the base survey's `kycCountries` (see `kycCountriesFixtures.ts`), nobody
 * has captured what this endpoint actually returns for the `onlyPep=true` response, so it's left as-is.
 */
const KYC_COUNTRIES = [
    { id: 'se', label: 'Sverige' },
    { id: 'no', label: 'Norge' },
    { id: 'dk', label: 'Danmark' },
    { id: 'fi', label: 'Finland' },
    { id: 'de', label: 'Tyskland' },
];

const PepQuestionsFull: Kyc = {
    templateVersion: 1,
    groups: [PEP_GROUP],
    kycCountries: KYC_COUNTRIES,
};

const KycQuestionsFullByCountry: Record<CountryCode.NO | CountryCode.DK, Kyc> = {
    [CountryCode.NO]: {
        templateVersion: 25,
        groups: [NEW_ACCOUNT_GROUP_NO, ECONOMY_GROUP_NO, CITIZENSHIP_GROUP_NO],
        kycCountries: KYC_COUNTRIES_NO,
    },
    [CountryCode.DK]: {
        templateVersion: 5,
        groups: [NEW_ACCOUNT_GROUP_DK, ECONOMY_GROUP_DK, CITIZENSHIP_GROUP_DK],
        kycCountries: KYC_COUNTRIES_DK,
    },
};

type UrlRequest = { url: string };
type CountryCodeParams = { countryCode?: string | readonly string[] };

export const getKnowYourCustomerResolver = async ({
    request,
    params,
}: {
    request: UrlRequest;
    params: CountryCodeParams;
}) => {
    const url = new URL(request.url);
    const onlyPep = url.searchParams.get('onlyPep') === 'true';

    if (onlyPep) {
        return HttpResponse.json(PepQuestionsFull);
    }

    // No capture exists yet for markets other than NO/DK, so they fall back to the NO fixture -- same behavior as
    // before this fixture was split by country.
    const countryCode = toCountryCode(typeof params.countryCode === 'string' ? params.countryCode : '');
    const fixture = KycQuestionsFullByCountry[countryCode === CountryCode.DK ? CountryCode.DK : CountryCode.NO];
    return HttpResponse.json(fixture);
};

export const getKnowYourCustomerHandlers = [
    http.get(
        '*/api/cl_loanapp_proxy_service/v1/:countryCode/know_your_customer/get_survey',
        getKnowYourCustomerResolver,
    ),
];
